import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

const DEFAULT_SCALE = 2

/**
 * Coleta pontos de corte e intervalos atômicos (ex.: bloco Informações).
 * @returns {{ points: number[], atomicRanges: { start: number, end: number }[], contentBottom: number }}
 */
function collectBreakMeta(element, ratio, selectors = {}) {
  const {
    rowSelector = '.ar-pdf-table tbody tr',
    summarySelector = '.ar-pdf-summary',
    footerSelector = '.ar-pdf-footer',
  } = selectors

  const containerTop = element.getBoundingClientRect().top
  const points = []
  const atomicRanges = []
  let contentBottom = 0

  const toY = (clientY) => Math.round((clientY - containerTop) * ratio)

  const addBottom = (el) => {
    if (!el) return 0
    const bottom = toY(el.getBoundingClientRect().bottom)
    if (bottom > 0) {
      points.push(bottom)
      contentBottom = Math.max(contentBottom, bottom)
    }
    return bottom
  }

  element.querySelectorAll(rowSelector).forEach(addBottom)
  addBottom(element.querySelector('.order-pdf-parties'))
  addBottom(element.querySelector('.order-pdf-header'))

  const summary = element.querySelector(summarySelector)
  if (summary) {
    const start = toY(summary.getBoundingClientRect().top)
    const end = toY(summary.getBoundingClientRect().bottom)
    if (end > start) {
      atomicRanges.push({ start, end })
      if (start > 0) points.push(start)
      points.push(end)
      contentBottom = Math.max(contentBottom, end)
    }
  }

  addBottom(element.querySelector(footerSelector))

  // Não usar scrollHeight: costuma incluir padding/espaço vazio e gera folha em branco.

  return {
    points: [...new Set(points)].sort((a, b) => a - b),
    atomicRanges,
    contentBottom,
  }
}

/**
 * Se o corte cair dentro de um bloco atômico, ajusta para antes ou depois do bloco inteiro.
 */
function adjustSliceEndForAtomic(sourceY, sliceEnd, maxY, pageHeightPx, atomicRanges, breakPoints) {
  let end = sliceEnd

  for (const range of atomicRanges) {
    const { start, end: rangeEnd } = range

    if (sourceY > start && sourceY < rangeEnd) {
      end = rangeEnd
      continue
    }

    if (end > start && end < rangeEnd) {
      if (sourceY <= start) {
        if (rangeEnd <= sourceY + pageHeightPx && rangeEnd <= maxY + 1) {
          end = rangeEnd
        } else {
          let before = sourceY
          for (const point of breakPoints) {
            if (point > sourceY && point <= start) before = point
          }
          end = before > sourceY ? before : start
        }
      } else {
        end = rangeEnd
      }
    }

    if (sourceY < rangeEnd && maxY > start && maxY < rangeEnd && end > start && end < rangeEnd) {
      if (sourceY <= start) {
        let before = sourceY
        for (const point of breakPoints) {
          if (point > sourceY && point <= start) before = point
        }
        end = before > sourceY ? before : start
      } else {
        end = rangeEnd
      }
    }
  }

  return end
}

function buildPageSlices(effectiveHeight, pageHeightPx, breakPoints, atomicRanges = []) {
  const slices = []
  let sourceY = 0

  while (sourceY < effectiveHeight - 1) {
    const maxY = Math.min(sourceY + pageHeightPx, effectiveHeight)
    let sliceEnd = sourceY

    for (const point of breakPoints) {
      if (point <= sourceY + 1) continue
      if (point > maxY) break
      const insideAtomic = atomicRanges.some(
        (r) => point > r.start + 1 && point < r.end - 1
      )
      if (insideAtomic) continue
      sliceEnd = point
    }

    sliceEnd = adjustSliceEndForAtomic(
      sourceY,
      sliceEnd,
      maxY,
      pageHeightPx,
      atomicRanges,
      breakPoints
    )

    if (sliceEnd <= sourceY) {
      sliceEnd = maxY
    }

    // Não criar fatia só com sobra vazia no fim do documento
    if (sliceEnd >= effectiveHeight - 1 && sourceY >= effectiveHeight - 2) {
      break
    }

    const height = sliceEnd - sourceY
    if (height <= 1) break

    slices.push({ sourceY, height })
    sourceY = sliceEnd
  }

  return slices
}

/**
 * Detecta fatia quase toda branca (folha em branco).
 */
function isSliceMostlyBlank(canvas, slice, sampleStep = 8) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return false

  const { sourceY, height } = slice
  const width = canvas.width
  let nonWhite = 0
  let samples = 0

  for (let y = sourceY; y < sourceY + height; y += sampleStep) {
    for (let x = 0; x < width; x += sampleStep) {
      const data = ctx.getImageData(x, y, 1, 1).data
      samples += 1
      // Tolerância a JPEG/anti-alias (não exige branco puro)
      if (data[0] < 250 || data[1] < 250 || data[2] < 250) {
        nonWhite += 1
        if (nonWhite > 12) return false
      }
    }
  }

  if (samples === 0) return true
  return nonWhite / samples < 0.002
}

/**
 * Captura um elemento HTML e gera PDF A4 multipágina para download.
 */
export async function exportElementToPdf(element, filename, options = {}) {
  if (!element) {
    throw new Error('Elemento para exportação não encontrado')
  }

  const pdf = await buildPdfFromElements([element], options)
  pdf.save(filename)
}

/**
 * Captura vários elementos (ex.: lotes de tabela) e une num único PDF A4.
 * Evita canvas gigante que o navegador corta em páginas brancas.
 */
export async function exportElementsToPdf(elements, filename, options = {}) {
  const list = (elements || []).filter(Boolean)
  if (!list.length) {
    throw new Error('Elemento para exportação não encontrado')
  }

  const pdf = await buildPdfFromElements(list, options)
  pdf.save(filename)
}

async function buildPdfFromElements(elements, options = {}) {
  const {
    orientation = 'p',
    scale = DEFAULT_SCALE,
    jpegQuality = 0.92,
    rowSelector,
    summarySelector,
    footerSelector,
  } = options

  const pdf = new jsPDF(orientation, 'mm', 'a4')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 8
  const contentWidth = pageWidth - margin * 2
  const contentHeight = pageHeight - margin * 2

  let pageIndex = 0

  for (const element of elements) {
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      backgroundColor: '#FFFFFF',
      logging: false,
      windowWidth: element.offsetWidth || 900,
      scrollX: 0,
      scrollY: 0,
    })

    const ratio = canvas.width / Math.max(element.offsetWidth || 1, 1)
    const { points: breakPoints, atomicRanges, contentBottom } = collectBreakMeta(element, ratio, {
      rowSelector,
      summarySelector,
      footerSelector,
    })

    const effectiveHeight = Math.min(
      canvas.height,
      contentBottom > 0 ? contentBottom : canvas.height
    )

    const trimmedBreakPoints = [...new Set(
      breakPoints.filter((point) => point > 0 && point <= effectiveHeight)
    )].sort((a, b) => a - b)

    if (
      trimmedBreakPoints.length === 0 ||
      trimmedBreakPoints[trimmedBreakPoints.length - 1] < effectiveHeight
    ) {
      trimmedBreakPoints.push(effectiveHeight)
    }

    const trimmedAtomic = atomicRanges
      .map((r) => ({
        start: Math.max(0, r.start),
        end: Math.min(effectiveHeight, r.end),
      }))
      .filter((r) => r.end > r.start)

    const pageHeightPx = (contentHeight / contentWidth) * canvas.width

    let slices = buildPageSlices(
      effectiveHeight,
      pageHeightPx,
      trimmedBreakPoints,
      trimmedAtomic
    ).filter((slice) => slice.height > 2)

    while (slices.length > 1 && isSliceMostlyBlank(canvas, slices[slices.length - 1])) {
      slices = slices.slice(0, -1)
    }

    for (const slice of slices) {
      if (pageIndex > 0) pdf.addPage()

      const pageCanvas = document.createElement('canvas')
      pageCanvas.width = canvas.width
      pageCanvas.height = slice.height
      const ctx = pageCanvas.getContext('2d')
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)

      ctx.drawImage(
        canvas,
        0, slice.sourceY, canvas.width, slice.height,
        0, 0, canvas.width, slice.height
      )

      const imgData = pageCanvas.toDataURL('image/jpeg', jpegQuality)
      const imgHeight = (slice.height * contentWidth) / canvas.width

      pdf.addImage(imgData, 'JPEG', margin, margin, contentWidth, imgHeight)
      pageIndex += 1
    }
  }

  if (pageIndex === 0) {
    throw new Error('Nenhum conteúdo para exportar no PDF')
  }

  return pdf
}
